import { useEffect, useState, type ReactNode } from 'react'
import Alert from '@mui/material/Alert'
import Button from '@mui/material/Button'
import FormControlLabel from '@mui/material/FormControlLabel'
import Stack from '@mui/material/Stack'
import Switch from '@mui/material/Switch'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import type { Document, Manifest } from '../../api/authoring'
import { declaresIntent } from '../../model/manifest'
import { pageFields } from '../../model/page'
import type { ResolvedRoute } from '../../model/website'
import { routeMenus } from '../../model/website'
import { MenuTags } from '../site/MenuTags'

/**
 * Everything an owner thinks of as "this page", in one form.
 *
 * **Routing lives on the site and fields live on the page**, which is
 * correct in the model and meaningless to somebody who thinks of the
 * slug, the menu name, the title and the descriptions as one thing. Build
 * mode keeps them apart because there the distinction is the point; here
 * they are one form, and each field's change is sent to whichever
 * component owns it.
 *
 * **Save sends only what changed**, and each change is its own intent
 * against its own aggregate — so renaming the page is one revision of
 * the Page and moving it in the menu is one revision of the Website,
 * exactly as it would be from the site editor. That is the rule this
 * client holds throughout: one thing an author meant, one intent, one
 * revision. What it does not do is batch them behind a pretence of
 * atomicity — if the second fails the first still happened, and the form
 * redraws from the documents rather than from what was typed.
 *
 * The parent is shown and not editable, deliberately. Moving a page
 * under a different one is a remove and an add against two levels, and
 * there is no single intent for it: a control that offered it would be
 * two commands wearing one label, with the page off the site in between
 * if the second failed.
 */
export interface PageSettingsValues {
  title: string
  /** The page's search description, `seo.description` on the Page. */
  seoDescription: string
  /** The page's search keywords, `seo.keywords` on the Page. */
  seoKeywords: string
  slug: string
  menuName: string
  /** The route's description, shown with the menu name. */
  description: string
  menus: string[]
  isHome: boolean
}

export function PageSettings({
  page,
  siteType,
  route,
  parent,
  menuOptions,
  manifest,
  busy,
  onSave,
}: {
  page: Document
  /** The site's type, for asking whether it still offers the route intents. */
  siteType: string
  /** The route that reaches this page, absent where nothing does. */
  route: ResolvedRoute | undefined
  /** The route this one sits under, for the sentence that says where it is. */
  parent: ResolvedRoute | undefined
  /**
   * The menus this site already uses, offered by the picker.
   *
   * Read from the whole routing tree rather than from this route,
   * because the point of offering them is the route that has not joined
   * one yet.
   */
  menuOptions: string[]
  manifest: Manifest | undefined
  busy: boolean
  onSave: (next: PageSettingsValues, was: PageSettingsValues) => void
}) {
  const stored = storedValues(page, route)
  const [draft, setDraft] = useState(stored)
  const key = JSON.stringify(stored)
  useEffect(() => setDraft(stored), [key])

  // Each control appears only where the type that owns it declares the
  // intent behind it — the same rule every other control in this client
  // follows, asked of the manifest rather than assumed.
  const canRetitle = declaresIntent(manifest, page.type, 'page.retitled')
  const canDescribe = declaresIntent(manifest, page.type, 'page.seo_set')
  const canRoute = Boolean(route) && declaresIntent(manifest, siteType, 'website.route_updated')
  const canSetHome = Boolean(route) && declaresIntent(manifest, siteType, 'website.home_set')

  const dirty = JSON.stringify(draft) !== key

  return (
    <Stack spacing={3}>
      {canRetitle && (
        <TextField
          label="Page title"
          value={draft.title}
          onChange={(event) => setDraft({ ...draft, title: event.target.value })}
          disabled={busy}
          fullWidth
        />
      )}

      {/* **The two search fields, together and said to be search
          fields.** They were loose in the same column as the title and
          the address, so the only thing distinguishing "Search
          description" from "Description" — which is the route's, means
          something else, and sits four controls below it — was the word
          "Search" at the front of one label. Under a heading they are
          one subject with two parts, and the route's description stops
          being read as the other half of this one.

          `seo.keywords` has been on the Page type all along and no
          control ever set it, so a page imported with keywords lost
          them at the next `page.seo_set` — this form rebuilt the whole
          `seo` object from the fields it drew. That is the bug the
          grouping made visible. */}
      {canDescribe && (
        <Section title="Search engines">
          <TextField
            label="Search description"
            value={draft.seoDescription}
            onChange={(event) => setDraft({ ...draft, seoDescription: event.target.value })}
            disabled={busy}
            fullWidth
            multiline
            minRows={2}
            helperText="The sentence search engines show under the page's name."
          />

          <TextField
            label="Keywords"
            value={draft.seoKeywords}
            onChange={(event) => setDraft({ ...draft, seoKeywords: event.target.value })}
            disabled={busy}
            fullWidth
            helperText="Optional, separated by commas. Whether they appear on the page depends on the site's design."
          />
        </Section>
      )}

      {canRoute && (
        <Section title="Address and menus">
          <TextField
            label="Address"
            value={draft.slug}
            onChange={(event) => setDraft({ ...draft, slug: event.target.value })}
            disabled={busy}
            fullWidth
            helperText={
              route ? `Visitors reach this page at ${route.urlPath}` : 'The last part of this page’s address.'
            }
          />

          <TextField
            label="Name in the menu"
            value={draft.menuName}
            onChange={(event) => setDraft({ ...draft, menuName: event.target.value })}
            disabled={busy}
            fullWidth
            helperText="What the menu calls this page. Usually the page title, or something shorter."
          />

          <TextField
            label="Description"
            value={draft.description}
            onChange={(event) => setDraft({ ...draft, description: event.target.value })}
            disabled={busy}
            fullWidth
            multiline
            minRows={2}
            helperText="Shown with the page's name in menus that have room for it."
          />

          <MenuTags
            value={draft.menus}
            options={menuOptions}
            onChange={(menus) => setDraft({ ...draft, menus })}
            disabled={busy}
          />

          {canSetHome && (
            <FormControlLabel
              control={
                <Switch
                  checked={draft.isHome}
                  onChange={(event) => setDraft({ ...draft, isHome: event.target.checked })}
                  disabled={busy || stored.isHome}
                />
              }
              label="This is the home page"
            />
          )}

          {canSetHome && draft.isHome && !stored.isHome && (
            <Alert severity="info">
              The current home page will stop being the home page. A site has only one.
            </Alert>
          )}
        </Section>
      )}

      <Typography variant="body2" color="text.secondary">
        {parent
          ? `It sits under ${parent.route.title || parent.route.slug}. To move it somewhere else, remove it from the menu there and add it where you want it.`
          : route
            ? 'It sits at the top of the menu.'
            : 'This page is not in any menu, so visitors can only reach it by its address.'}
      </Typography>

      <Stack direction="row" spacing={1}>
        <Button variant="contained" disabled={busy || !dirty} onClick={() => onSave(draft, stored)}>
          Save
        </Button>
        <Button disabled={busy || !dirty} onClick={() => setDraft(stored)}>
          Discard
        </Button>
      </Stack>
    </Stack>
  )
}

/** What the two documents currently say, under the names this form uses. */
function storedValues(page: Document, route: ResolvedRoute | undefined): PageSettingsValues {
  const fields = pageFields(page)
  return {
    title: fields.title ?? '',
    seoDescription: fields.seo?.description ?? '',
    seoKeywords: fields.seo?.keywords ?? '',
    slug: route?.route.slug ?? '',
    menuName: route?.route.title ?? '',
    description: route?.route.description ?? '',
    menus: routeMenus(route?.route),
    isHome: route?.isHome ?? false,
  }
}

/**
 * A run of controls that are one subject, with that subject named.
 *
 * A form of eight fields in one column is eight unrelated questions;
 * the same eight under three headings is three decisions. The heading
 * is an overline rather than a `Paper` or an accordion deliberately —
 * everything here is short, and a box around four fields buys a border
 * at the cost of the width they were given.
 */
function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <Stack spacing={2}>
      <Typography variant="overline" color="text.secondary">
        {title}
      </Typography>
      {children}
    </Stack>
  )
}
